import { useState } from "react";
import type { PlatformLoginState } from "@probetalab/contracts";
import { post } from "../lib/api.js";
import { fmtDateTime } from "../lib/format.js";
import { useSession } from "../lib/session.js";
import { useToast } from "../lib/toast.js";
import { errorMessage } from "../lib/useQuery.js";
import { Alert, Button, Card, Chip, Field, Sheet, Switch, Textarea } from "./ui.js";

/**
 * Operators only (ADR 0025, proposal §8.3): may this laboratory's staff sign in on the platform
 * host? Off by default; the laboratory's own address is where its staff belong. Turning it on is
 * for a pinch (their domain failing, a device not yet moved). Every change asks for a reason and a
 * confirmation, and is written to the laboratory's activity log as liveratti's support.
 */
export function PlatformLoginCard() {
  const { me, refresh } = useSession();
  const toast = useToast();
  const state = me?.operator?.platform_login;
  const [target, setTarget] = useState<boolean | null>(null);
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  if (!me || !state) return null;

  const host = state.platform_host;
  const lab = me.lab_display_name;
  const labHost = new URL(me.preferred_origin).host;
  const valid = reason.trim().length >= 3;

  function open(next: boolean) {
    setReason("");
    setTarget(next);
  }

  async function confirm() {
    if (target === null || !valid) return;
    setBusy(true);
    try {
      const r: PlatformLoginState = await post("operatorPlatformLogin", { enabled: target, reason: reason.trim() });
      await refresh();
      toast.success(r.enabled ? `El personal de ${lab} ya puede iniciar sesión en ${host}.` : `El personal de ${lab} inicia sesión solo en ${labHost}.`);
      setTarget(null);
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card title={`Acceso del personal por ${host}`} actions={<Chip tone={state.enabled ? "amber" : "neutral"} dot>{state.enabled ? "Activado" : "Desactivado"}</Chip>}>
      <div className="stack-lg">
        <p className="muted" style={{ margin: 0 }}>
          El personal de {lab} inicia sesión en <strong>{labHost}</strong>. Activa esto solo si necesitan entrar por {host} mientras tanto; el soporte siempre puede entrar.
        </p>
        <Switch checked={state.enabled} onChange={(v) => open(v)}>
          Permitir que el personal de {lab} inicie sesión en {host}
        </Switch>
        {state.changed_at && (
          <p className="small muted" style={{ margin: 0 }}>
            Último cambio: {fmtDateTime(state.changed_at)}
            {state.changed_by ? ` · ${state.changed_by}` : ""}
            {state.reason ? ` · «${state.reason}»` : ""}
          </p>
        )}
      </div>
      <Sheet
        open={target !== null}
        onClose={() => setTarget(null)}
        title={target ? `¿Permitir el acceso por ${host}?` : `¿Quitar el acceso por ${host}?`}
        footer={
          <>
            <Button variant="ghost" onClick={() => setTarget(null)}>
              Cancelar
            </Button>
            <Button variant={target ? "amber" : "primary"} icon="check" busy={busy} disabled={!valid} onClick={() => void confirm()}>
              {target ? "Permitir acceso" : "Quitar acceso"}
            </Button>
          </>
        }
      >
        <div className="stack-lg">
          {target ? (
            <Alert tone="amber">
              El personal de {lab} podrá iniciar sesión en {host} además de {labHost}. Quítalo en cuanto ya no haga falta.
            </Alert>
          ) : (
            <Alert tone="blue">
              Las sesiones abiertas del personal de {lab} en {host} se cierran al momento; al intentar entrar ahí verán la dirección {labHost}.
            </Alert>
          )}
          <Field label="Motivo" hint="Queda en la bitácora del laboratorio.">
            <Textarea rows={3} maxLength={500} value={reason} onChange={(e) => setReason(e.target.value)} placeholder={target ? "Ej. su dominio no responde; volver a quitarlo al resolverlo." : "Ej. ya instalaron su dirección en la tableta."} autoFocus />
          </Field>
        </div>
      </Sheet>
    </Card>
  );
}
